// Imports
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import Checkbox from '../../../../../../../shareables/foundation/front-end/components/forms/fields/checkbox';
import MultiCheckboxContainer from '../../../../../../../shareables/foundation/front-end/components/forms/multi-checkbox-container';
import commonActions from '../../../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import APIButton, { APIButtonOptions } from '../../../../../../../shareables/integrations/api/components/button';
import APIForm, { APIFormOptions } from '../../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetPrivacyResponse } from '../../../../../../../shareables/types/api/main/website/account/settings/privacy/get';
import { PutPrivacyParameters } from '../../../../../../../shareables/types/api/main/website/account/settings/privacy/put';


// Styled components
const Container = styled.div`
	line-height: 1.3125rem;
`;

const DeleteAccountPanel = styled.div`
	border: 0.0625rem solid #ebccd1;
	border-radius: 0.25rem;
	box-shadow: 0 0.0625rem 0.0625rem rgba(0, 0, 0, 0.05);
	margin-top: 2.5rem;
`;

const DeleteAccountHeading = styled.div`
	color: #a94442;
	background-color: #f2dede;
	padding: 0.625rem 0.9375rem;
	border-top-left-radius: 0.1875rem;
	border-top-right-radius: 0.1875rem;
`;

const DeleteAccountBody = styled.div`
	padding: 0.9375rem;
`;

const DeleteButtonWrapper = styled.div`
	margin-top: 2rem;
`;


// Define the accepted props
interface Props {
	storybookPrivacySettings?: GetPrivacyResponse;
}


// Function component
const AccountPrivacy: React.FC<Props> = ({ storybookPrivacySettings }) => {
	// Use state
	const [privacySettings, setPrivacySettings] = useState<GetPrivacyResponse | null>(storybookPrivacySettings ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load privacy settings on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetPrivacyResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/account/settings/privacy`,
				completion: (json) => {
					setPrivacySettings(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// If loading, then display nothing
	if (privacySettings === null) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<PutPrivacyParameters, undefined, undefined> = {
		action: 'PUT',
		uri: `/website/${window.location.hostname}/account/settings/privacy`,
		submitButton: {
			text: 'Save',
		},
		recoverState: false,
	};
	
	
	// Build button options
	const buttonOptions: APIButtonOptions<undefined, undefined, undefined> = {
		action: 'DELETE',
		uri: `/website/${window.location.hostname}/account`,
		warning: {
			message: 'Deleting your account is permanent and cannot be undone.',
			button: 'Yes, delete my account',
		},
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		dispatch(
			commonActions.showPageMessage({
				color: 'success',
				title: 'Success',
				message: 'These settings have been saved.',
			})
		);
	};
	
	
	// Handle account deletion
	const handleAccountDeletion = () => {
		window.location.href = `https://${window.location.hostname}/oauth/sign-out?redirect-uri=${encodeURIComponent('/')}`;
	};
	
	
	// Return JSX
	return (
		<Container>
			<APIForm options={formOptions} onCompletion={handleFormCompletion}>
				<h4>Hide this information from the resident directory…</h4>
				<MultiCheckboxContainer>
					<Checkbox name='hidePhones' label='Home phone numbers' initiallyChecked={privacySettings.hidePhones} />
					<Checkbox
						name='hideInternalAddresses'
						label='Addresses located inside your community'
						initiallyChecked={privacySettings.hideInternalAddresses}
					/>
					<Checkbox
						name='hideExternalAddresses'
						label='Addresses located outside your community'
						initiallyChecked={privacySettings.hideExternalAddresses}
					/>
					<Checkbox
						name='hideAccount'
						label='Hide my account completely from the directory'
						initiallyChecked={privacySettings.hideAccount}
					/>
				</MultiCheckboxContainer>
			</APIForm>
			
			<DeleteAccountPanel>
				<DeleteAccountHeading>
					<h3>
						<FontAwesomeIcon icon={faExclamationCircle} /> Delete account
					</h3>
				</DeleteAccountHeading>
				<DeleteAccountBody>
					Click the button below to delete your account on this website. This will destroy everything contained within
					your account, including other members of your account, contact information, profile information, preferences,
					etc. This is a permanent action and cannot be undone.
					<DeleteButtonWrapper>
						<APIButton options={buttonOptions} color='danger' smaller onCompletion={handleAccountDeletion}>
							Delete my account
						</APIButton>
					</DeleteButtonWrapper>
				</DeleteAccountBody>
			</DeleteAccountPanel>
		</Container>
	);
};

export default AccountPrivacy;
